<template>
  <div>
    <v-container fluid>
      <v-toolbar color="primary" class="header-title text-white">
        <v-toolbar-title class="title">Tax Sandbox</v-toolbar-title>
        <v-spacer />
        <div class="d-flex pr-4">
          <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
        </div>
      </v-toolbar>

      <div class="cfg-breadcrumb">
        <span>LH Bank Configuration</span>
        <v-icon size="14" class="cfg-breadcrumb-sep">mdi-chevron-right</v-icon>
        <span class="cfg-breadcrumb-current">Tax Sandbox</span>
      </div>

      <v-card class="card2 sandbox-source-card">
        <v-card-text>
          <div class="sandbox-source-label">
            <v-icon size="18">mdi-cog-outline</v-icon>
            <span>Tax Config Source</span>
          </div>
          <div class="sandbox-source-grid">
            <label class="sandbox-source-field">
              <v-icon size="18">mdi-calendar</v-icon>
              <span>Tax Year</span>
              <select :value="selectedTaxYear" @change="selectedYear = $event.target.value">
                <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
              </select>
            </label>

            <div class="sandbox-source-field is-readonly">
              <v-icon size="18">mdi-format-list-checks</v-icon>
              <span>Active Rules</span>
              <strong>{{ rulesForForm.length }}/{{ taxRules.length }}</strong>
            </div>

            <router-link class="sandbox-config-link" to="/TaxConfiguration">
              <v-icon size="17">mdi-open-in-new</v-icon>
              Open config
            </router-link>
          </div>
        </v-card-text>
      </v-card>

      <div class="sandbox-layout">
        <section class="sandbox-main">
          <div class="sandbox-mobile-frame">
            <div class="sandbox-phone-status">
              <span>9:41</span>
              <span>Tax Planning</span>
              <span>100%</span>
            </div>

            <div class="sandbox-phone-chat">
              <div class="sandbox-bot-bubble">
                ทดสอบฟอร์มกรอกภาษีตาม config ที่เลือก
              </div>

              <div class="sandbox-widget group-card">
                <div class="deduction-progress">
                  <span>{{ pageMeta.icon }} {{ pageMeta.title }} ({{ currentPage + 1 }}/{{ totalPages }})</span>
                  <div class="progress-bar">
                    <div :style="{ width: progressWidth }" />
                  </div>
                </div>

                <div class="group-items">
                  <div v-if="currentPage === FORM_PAGE" class="form-type-list">
                    <button
                      v-for="option in formOptions"
                      :key="option.value"
                      :class="['form-type-btn', { selected: formType === option.value }]"
                      data-testid="sandbox-form-type"
                      type="button"
                      @click="handleFormChange(option.value)"
                    >
                      <span class="form-type-icon">{{ option.icon }}</span>
                      <span class="form-type-text">
                        <span class="form-type-title">{{ option.label }}</span>
                        <span class="form-type-desc">{{ option.description }}</span>
                      </span>
                      <span class="form-type-check">{{ formType === option.value ? '✓' : '' }}</span>
                    </button>
                  </div>

                  <template v-if="currentPage === INCOME_PAGE">
                    <div v-for="field in visibleIncomeFields" :key="field.key" class="group-item amount-item">
                      <label class="item-label">
                        {{ field.label }}
                        <span class="income-sublabel">{{ field.subLabel }}</span>
                      </label>
                      <input
                        :data-testid="`sandbox-${field.key}`"
                        type="text"
                        inputmode="numeric"
                        placeholder="0"
                        :value="toNumericInput(taxInput[field.key])"
                        @input="updateAmount(field.key, $event.target.value)"
                      />

                      <div v-if="field.expenseChoices" class="expense-choice-section">
                        <span class="expense-choice-label">หักค่าใช้จ่าย</span>
                        <select
                          class="expense-choice-select"
                          :value="getExpenseChoiceValue(field.key)"
                          @change="handleExpenseChoiceChange(field.key, $event.target.value)"
                        >
                          <option v-for="choice in field.expenseChoices" :key="choice.value" :value="choice.value">
                            {{ choice.label }}
                          </option>
                        </select>
                        <input
                          v-if="isActualExpense(field.key)"
                          class="expense-actual-input"
                          type="text"
                          inputmode="numeric"
                          placeholder="จำนวนเงินจริง"
                          :data-testid="`sandbox-${getActualExpenseKey(field.key)}`"
                          :value="toNumericInput(taxInput[getActualExpenseKey(field.key)])"
                          @input="updateAmount(getActualExpenseKey(field.key), $event.target.value)"
                        />
                      </div>
                    </div>
                  </template>

                  <template v-if="currentPage === EXPENSE_PAGE">
                    <template v-if="expenseItems.length">
                      <div v-for="item in expenseItems" :key="item.label" class="group-item auto-item">
                        <div class="group-item-header">
                          <span class="item-label">{{ item.label }}</span>
                          <span class="auto-badge">อัตโนมัติ</span>
                        </div>
                        <span class="auto-amount">{{ formatNum(item.amount) }} บาท</span>
                      </div>
                      <div class="expense-total-row">
                        <span>รวมค่าใช้จ่ายทั้งหมด</span>
                        <strong>{{ formatNum(currentResult.expenseDeduction) }} บาท</strong>
                      </div>
                    </template>
                    <div v-else class="group-item auto-item">
                      <span class="item-label muted">ไม่มีรายได้ที่หักค่าใช้จ่ายได้</span>
                    </div>
                  </template>

                  <template v-if="activeDeductionGroup">
                    <div
                      v-for="item in activeDeductionGroup.items"
                      :key="item.key || item.label"
                      :class="['group-item', `${item.type}-item`]"
                    >
                      <template v-if="item.type === 'auto'">
                        <div class="group-item-header">
                          <span class="item-label">{{ item.label }}</span>
                          <span class="auto-badge">อัตโนมัติ</span>
                        </div>
                        <span class="auto-amount">{{ formatNum(item.amount) }} บาท</span>
                      </template>

                      <template v-else-if="item.type === 'amount'">
                        <label class="item-label">
                          {{ item.label }}
                          <span v-if="item.max" class="max-hint">สูงสุด {{ formatNum(item.max) }}</span>
                        </label>
                        <input
                          :data-testid="`sandbox-${item.key}`"
                          type="text"
                          inputmode="numeric"
                          placeholder="0"
                          :value="toNumericInput(taxInput[item.key])"
                          @input="updateAmount(item.key, $event.target.value)"
                        />
                      </template>

                      <template v-else-if="item.type === 'number'">
                        <span class="item-label">{{ item.label }}</span>
                        <div v-for="field in item.fields" :key="field.key" class="field-row">
                          <label>{{ field.label }}</label>
                          <input
                            :data-testid="`sandbox-${field.key}`"
                            type="text"
                            inputmode="numeric"
                            :value="toNumericInput(taxInput[field.key])"
                            @input="updateNumber(field.key, $event.target.value)"
                          />
                        </div>
                      </template>

                      <template v-else-if="item.type === 'checkbox'">
                        <span class="item-label">{{ item.label }}</span>
                        <div class="checkbox-group">
                          <label v-for="field in item.fields" :key="field.key" class="checkbox-label">
                            <input
                              :data-testid="`sandbox-${field.key}`"
                              type="checkbox"
                              :checked="Boolean(taxInput[field.key])"
                              @change="updateBoolean(field.key, $event.target.checked)"
                            />
                            <span>{{ field.label }}</span>
                          </label>
                        </div>
                      </template>
                    </div>
                  </template>

                  <div v-if="currentPage === WITHHOLDING_PAGE" class="group-item amount-item">
                    <label class="item-label">
                      ภาษีหัก ณ ที่จ่าย ที่จ่ายไปแล้ว{{ formType === '94' ? ' (ม.ค.-มิ.ย.)' : 'ทั้งปี' }}
                    </label>
                    <input
                      data-testid="sandbox-withholdingTax"
                      type="text"
                      inputmode="numeric"
                      placeholder="เช่น 10,000"
                      :value="toNumericInput(taxInput.withholdingTax)"
                      @input="updateAmount('withholdingTax', $event.target.value)"
                    />
                  </div>
                </div>

                <div class="deduction-actions">
                  <button
                    v-if="currentPage === FORM_PAGE"
                    class="skip-btn"
                    data-testid="sandbox-reset-btn"
                    type="button"
                    @click="resetForm"
                  >
                    รีเซ็ต
                  </button>
                  <button v-else class="skip-btn" type="button" @click="handleBack">ย้อนกลับ</button>
                  <button
                    data-testid="sandbox-calculate-btn"
                    type="button"
                    :disabled="!canNext || currentPage === WITHHOLDING_PAGE"
                    @click="handleNext"
                  >
                    {{ currentPage === WITHHOLDING_PAGE ? 'พร้อมคำนวณ' : 'ถัดไป' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="sandbox-home-indicator" />
          </div>
        </section>

        <aside class="sandbox-result-panel">
          <div class="sandbox-result-card" data-testid="sandbox-result-card">
            <span>ผลคำนวณ</span>
            <strong>{{ currentResult.taxToPay > 0 ? `${formatNum(currentResult.taxToPay)} บาท` : `${formatNum(currentResult.taxRefund)} บาท` }}</strong>
            <p>{{ currentResult.taxToPay > 0 ? 'ภาษีที่ต้องชำระเพิ่ม' : 'ภาษีที่คาดว่าจะได้คืน' }}</p>
          </div>

          <div class="sandbox-result-list">
            <div v-for="item in resultSummaryItems" :key="item.label">
              <span>{{ item.label }}</span>
              <strong>{{ formatNum(item.value) }}</strong>
            </div>
          </div>

          <div class="sandbox-config-summary">
            <h3>Tax Config ที่ใช้งานอยู่</h3>
            <div class="sandbox-config-kpis">
              <div>
                <span>Year</span>
                <strong>{{ selectedTaxYear }}</strong>
              </div>
              <div>
                <span>Rules</span>
                <strong>{{ rulesForForm.length }}/{{ taxRules.length }}</strong>
              </div>
              <div>
                <span>Sections</span>
                <strong>{{ deductionGroups.length }}</strong>
              </div>
            </div>
            <p>ดึงจาก Tax Config store ของปีที่เลือก และกรองเฉพาะ rules ที่ active และตรงกับแบบ ภ.ง.ด.</p>
            <div class="sandbox-category-list">
              <span v-for="category in categorySummary" :key="category.code">{{ category.icon }} {{ category.label }}</span>
            </div>
          </div>

          <div class="sandbox-scenarios-card" data-testid="sandbox-scenarios-card">
            <div class="sandbox-scenarios-head">
              <h3>Saved Scenarios</h3>
              <span>{{ scenarios.length }}</span>
            </div>
            <p v-if="scenarios.length === 0" class="sandbox-empty">No saved scenarios yet</p>
            <button
              v-for="scenario in scenarios"
              :key="scenario.id"
              :data-testid="`sandbox-scenario-${scenario.id}`"
              class="sandbox-scenario-row"
              type="button"
              @click="loadScenario(scenario)"
            >
              <span>
                <strong>{{ scenario.name }}</strong>
                <em>{{ scenario.formType === '94' ? 'ภ.ง.ด. 94' : 'ภ.ง.ด. 90/91' }} · {{ formatDate(scenario.updatedAt) }}</em>
              </span>
              <v-btn icon variant="text" size="small" @click.stop="deleteScenarioConfirm(scenario)">
                <v-icon size="18" color="error">mdi-delete-outline</v-icon>
              </v-btn>
            </button>
            <v-btn
              data-testid="sandbox-save-scenario-btn"
              color="success"
              variant="outlined"
              block
              prepend-icon="mdi-content-save"
              class="mt-3"
              @click="openSaveDialog"
            >
              Save Scenario
            </v-btn>
          </div>
        </aside>
      </div>

      <v-dialog v-model="saveDialog" max-width="400">
        <v-card>
          <v-card-title>Save Scenario</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="scenarioName"
              data-testid="sandbox-scenario-name-input"
              label="Scenario Name"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
              autofocus
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="saveDialog = false">Cancel</v-btn>
            <v-btn
              data-testid="sandbox-scenario-save-confirm"
              color="primary"
              variant="flat"
              :disabled="!scenarioName.trim()"
              @click="saveScenario"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="deleteDialog" max-width="360">
        <v-card>
          <v-card-title>Delete Scenario</v-card-title>
          <v-card-text>
            Are you sure you want to delete "{{ scenarioToDelete?.name }}"?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
            <v-btn
              data-testid="sandbox-delete-confirm-btn"
              color="error"
              variant="flat"
              @click="confirmDelete"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="bottom right">
        {{ snackbar.text }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import { getDefaultTaxInput, calculateTax, formatNumber } from '../utils/taxCalculator.js';
import {
  getScenarios,
  createScenario,
  deleteScenario,
  clearResult,
} from '../utils/taxSandboxStore.js';
import { taxConfigStore } from '../utils/taxConfigStore.js';

const FORM_PAGE = 0;
const INCOME_PAGE = 1;
const EXPENSE_PAGE = 2;
const DEDUCTION_START = 3;

const CATEGORY_ICONS = {
  A: '👨‍👩‍👧‍👦',
  B: '🛡️',
  C: '📈',
  D: '🏠',
  E: '🌱',
  F: '🎗️',
  G: '🧾',
  H: '📌',
};

export default {
  name: 'TaxSandbox',
  data() {
    return {
      FORM_PAGE,
      INCOME_PAGE,
      EXPENSE_PAGE,
      today: new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }),
      selectedYear: '',
      formType: '90',
      currentPage: FORM_PAGE,
      taxInput: getDefaultTaxInput(),
      saveDialog: false,
      deleteDialog: false,
      scenarioName: '',
      scenarioToDelete: null,
      snackbar: { show: false, text: '', color: 'success' },
      rules: {
        required: (v) => !!v || 'Required',
      },
      formOptions: [
        {
          value: '90',
          label: 'เต็มปี',
          icon: '📋',
          description: 'ภ.ง.ด. 90 / 91 - ยื่นรายได้ทั้งปี ม.ค.-ธ.ค.',
          pnd: ['PND90', 'PND91'],
        },
        {
          value: '94',
          label: 'กลางปี',
          icon: '📅',
          description: 'ภ.ง.ด. 94 - ยื่นรายได้ครึ่งปีแรก ม.ค.-มิ.ย.',
          pnd: ['PND94'],
        },
      ],
      incomeFields: [
        { key: 'income40_1', label: 'ม.40(1) เงินเดือน/โบนัส', subLabel: 'เงินเดือน ค่าจ้าง โบนัส เบี้ยเลี้ยง' },
        { key: 'income40_2', label: 'ม.40(2) ค่านายหน้า/ค่าจ้างอิสระ', subLabel: 'ค่านายหน้า ค่าธรรมเนียม รับจ้างทั่วไป' },
        { key: 'income40_3', label: 'ม.40(3) ค่าลิขสิทธิ์/กู๊ดวิลล์', subLabel: 'ค่าลิขสิทธิ์ ค่าสิทธิ์ และทรัพย์สินทางปัญญา' },
        { key: 'income40_4', label: 'ม.40(4) ดอกเบี้ย/เงินปันผล', subLabel: 'ดอกเบี้ย เงินปันผล ส่วนแบ่งกำไร' },
        {
          key: 'income40_5',
          label: 'ม.40(5) ค่าเช่าทรัพย์สิน',
          subLabel: 'ค่าเช่าบ้าน ที่ดิน ยานพาหนะ หรือทรัพย์สินอื่น',
          expenseChoices: [
            { value: 'flat_10', label: 'เหมา 10%' },
            { value: 'flat_20', label: 'เหมา 20%' },
            { value: 'flat_30', label: 'เหมา 30%' },
            { value: 'actual', label: 'ตามจริง' },
          ],
        },
        {
          key: 'income40_6',
          label: 'ม.40(6) วิชาชีพอิสระ',
          subLabel: 'แพทย์ ทนาย วิศวกร สถาปนิก บัญชี ประณีตศิลป์',
          expenseChoices: [
            { value: 'flat_30', label: 'เหมา 30%' },
            { value: 'flat_60', label: 'เหมา 60%' },
            { value: 'actual', label: 'ตามจริง' },
          ],
        },
        {
          key: 'income40_7',
          label: 'ม.40(7) รับเหมาก่อสร้าง',
          subLabel: 'รับเหมาที่ผู้รับเหมาจัดหาสัมภาระเอง',
          expenseChoices: [
            { value: 'flat_60', label: 'เหมา 60%' },
            { value: 'actual', label: 'ตามจริง' },
          ],
        },
        {
          key: 'income40_8',
          label: 'ม.40(8) ธุรกิจ/การค้า/อื่นๆ',
          subLabel: 'ธุรกิจ พาณิชย์ เกษตร อุตสาหกรรม ขนส่ง และอื่นๆ',
          expenseChoices: [
            { value: 'flat_60', label: 'เหมา 60%' },
            { value: 'actual', label: 'ตามจริง' },
          ],
        },
      ],
    };
  },
  computed: {
    scenarios() {
      return getScenarios();
    },
    yearOptions() {
      const years = taxConfigStore.getYears().map((row) => row.taxYear);
      return years.length ? years : ['TY2026'];
    },
    selectedTaxYear() {
      return this.selectedYear || this.yearOptions[0] || 'TY2026';
    },
    selectedVersion() {
      const versions = taxConfigStore.getVersions(this.selectedTaxYear);
      return versions.find((version) => version.isActive) || versions[0] || null;
    },
    taxRules() {
      return this.selectedVersion?.rules || [];
    },
    selectedPndCodes() {
      return this.formOptions.find((option) => option.value === this.formType)?.pnd || [];
    },
    rulesForForm() {
      return this.taxRules.filter((rule) => {
        const pnd = Array.isArray(rule.pnd) ? rule.pnd : [];
        return rule.active !== false && pnd.some((code) => this.selectedPndCodes.includes(code));
      });
    },
    visibleIncomeFields() {
      if (this.formType === '91') return this.incomeFields.filter((field) => field.key === 'income40_1');
      return this.incomeFields;
    },
    currentResult() {
      return calculateTax(this.taxInput);
    },
    totalIncome() {
      return this.visibleIncomeFields.reduce((sum, field) => sum + (Number(this.taxInput[field.key]) || 0), 0);
    },
    expenseItems() {
      const items = [];
      const input = this.taxInput;
      const exp12 = Math.min((input.income40_1 + input.income40_2) * 0.5, 100000);
      if (exp12 > 0) items.push({ label: 'ม.40(1)+(2) หัก 50% (สูงสุด 100,000)', amount: exp12 });

      const exp3 = Math.min(input.income40_3 * 0.5, 100000);
      if (exp3 > 0) items.push({ label: 'ม.40(3) หัก 50% (สูงสุด 100,000)', amount: exp3 });

      const addExpense = (income, actualExpense, rate, label) => {
        if (income <= 0) return;
        const amount = actualExpense ?? (income * rate / 100);
        const tag = actualExpense !== null ? 'ตามจริง' : `เหมา ${rate}%`;
        items.push({ label: `${label} หัก${tag}`, amount });
      };

      addExpense(input.income40_5, input.expense40_5, input.expense40_5_rate, 'ม.40(5)');
      addExpense(input.income40_6, input.expense40_6, input.expense40_6_rate, 'ม.40(6)');
      addExpense(input.income40_7, input.expense40_7, 60, 'ม.40(7)');
      addExpense(input.income40_8, input.expense40_8, 60, 'ม.40(8)');
      return items;
    },
    deductionGroups() {
      const personalAmount = this.formType === '94' ? 30000 : 60000;
      return [
        {
          id: 'family',
          icon: '👨‍👩‍👧‍👦',
          title: 'ครอบครัว',
          items: [
            { type: 'auto', label: 'ค่าลดหย่อนส่วนตัว', amount: personalAmount },
            {
              type: 'checkbox',
              label: 'คู่สมรสไม่มีเงินได้',
              fields: [{ key: 'spouseNoIncome', label: `ใช้สิทธิ์ ${this.formatNum(personalAmount)} บาท` }],
            },
            { type: 'amount', key: 'pregnancyCost', label: 'ค่าฝากครรภ์/ทำคลอด', max: this.formType === '94' ? 30000 : 60000 },
            {
              type: 'number',
              label: 'ค่าลดหย่อนบุตร',
              fields: [
                { key: 'childrenBefore2561', label: 'จำนวนบุตรทั่วไป' },
                { key: 'childrenAfter2561', label: 'จำนวนบุตรเกิดปี 2561 เป็นต้นไป' },
              ],
            },
          ],
        },
        {
          id: 'parents',
          icon: '🧓',
          title: 'บิดามารดาและผู้พิการ',
          items: [
            {
              type: 'checkbox',
              label: 'บิดามารดา',
              fields: [
                { key: 'deductFather', label: 'บิดา' },
                { key: 'deductMother', label: 'มารดา' },
                { key: 'deductSpouseFather', label: 'บิดาคู่สมรส' },
                { key: 'deductSpouseMother', label: 'มารดาคู่สมรส' },
              ],
            },
            {
              type: 'checkbox',
              label: 'ผู้พิการ/ทุพพลภาพ',
              fields: [
                { key: 'disabledSpouse', label: 'คู่สมรส' },
                { key: 'disabledFather', label: 'บิดา' },
                { key: 'disabledMother', label: 'มารดา' },
                { key: 'disabledChild', label: 'บุตร' },
                { key: 'disabledOther', label: 'อื่นๆ' },
              ],
            },
          ],
        },
        {
          id: 'insurance',
          icon: '🛡️',
          title: 'ประกัน/กองทุน',
          items: [
            { type: 'amount', key: 'socialSecurity', label: 'เงินประกันสังคม', max: 10500 },
            { type: 'amount', key: 'lifeInsurance', label: 'เบี้ยประกันชีวิตทั่วไป', max: this.formType === '94' ? 10000 : 100000 },
            { type: 'amount', key: 'healthInsurance', label: 'เบี้ยประกันสุขภาพตนเอง', max: 25000 },
            { type: 'amount', key: 'parentHealthInsurance', label: 'เบี้ยประกันสุขภาพบิดามารดา', max: 15000 },
            { type: 'amount', key: 'pensionInsurance', label: 'ประกันชีวิตแบบบำนาญ', max: 200000 },
            { type: 'amount', key: 'rmf', label: 'กองทุน RMF', max: 500000 },
            { type: 'amount', key: 'pvd', label: 'กบข./กองทุนสำรองเลี้ยงชีพ', max: 500000 },
            { type: 'amount', key: 'nsf', label: 'กองทุนการออมแห่งชาติ', max: 30000 },
          ],
        },
        {
          id: 'housing',
          icon: '🏠',
          title: 'ที่อยู่อาศัย/ลงทุน',
          items: [
            { type: 'amount', key: 'housingInterest', label: 'ดอกเบี้ยซื้อที่อยู่อาศัย', max: 100000 },
            { type: 'amount', key: 'homeBuild', label: 'ค่าจ้างก่อสร้างบ้าน', max: 100000 },
            { type: 'amount', key: 'socialEnterprise', label: 'เงินลงทุน Social Enterprise', max: 100000 },
            { type: 'amount', key: 'thaiESG', label: 'กองทุน Thai ESG', max: 300000 },
            { type: 'amount', key: 'thaiESGX', label: 'กองทุน Thai ESGX', max: 300000 },
            { type: 'amount', key: 'ltfToESGX', label: 'สับเปลี่ยน LTF ไป Thai ESGX' },
          ],
        },
        {
          id: 'donation',
          icon: '🎗️',
          title: 'บริจาค/มาตรการรัฐ',
          items: [
            { type: 'amount', key: 'politicalDonation', label: 'บริจาคพรรคการเมือง', max: 10000 },
            { type: 'amount', key: 'educationDonation', label: 'บริจาคเพื่อการศึกษาฯ (2 เท่า)' },
            { type: 'amount', key: 'generalDonation', label: 'บริจาคทั่วไป' },
            { type: 'amount', key: 'easyEReceipt', label: 'Easy E-Receipt 2.0', max: 50000 },
            { type: 'amount', key: 'travelSecondary', label: 'เที่ยวดีมีคืน เมืองรอง', max: 15000 },
            { type: 'amount', key: 'travelOther', label: 'เที่ยวดีมีคืน จังหวัดอื่น', max: 15000 },
          ],
        },
      ];
    },
    WITHHOLDING_PAGE() {
      return DEDUCTION_START + this.deductionGroups.length;
    },
    totalPages() {
      return this.WITHHOLDING_PAGE + 1;
    },
    progressWidth() {
      return `${((this.currentPage + 1) / this.totalPages) * 100}%`;
    },
    activeDeductionGroup() {
      if (this.currentPage < DEDUCTION_START || this.currentPage >= this.WITHHOLDING_PAGE) return null;
      return this.deductionGroups[this.currentPage - DEDUCTION_START] || null;
    },
    pageMeta() {
      if (this.currentPage === FORM_PAGE) return { title: 'เลือกประเภทแบบภาษี', icon: '📄' };
      if (this.currentPage === INCOME_PAGE) return { title: 'รายได้', icon: '💰' };
      if (this.currentPage === EXPENSE_PAGE) return { title: 'ค่าใช้จ่าย', icon: '📋' };
      if (this.activeDeductionGroup) return { title: this.activeDeductionGroup.title, icon: this.activeDeductionGroup.icon };
      return { title: 'ภาษีหัก ณ ที่จ่าย', icon: '🧾' };
    },
    canNext() {
      return this.currentPage !== INCOME_PAGE || this.totalIncome > 0;
    },
    resultSummaryItems() {
      return [
        { label: 'รายได้รวม', value: this.currentResult.totalIncome },
        { label: 'ค่าใช้จ่าย', value: this.currentResult.expenseDeduction },
        { label: 'ค่าลดหย่อน', value: this.currentResult.totalDeductions },
        { label: 'เงินได้สุทธิ', value: this.currentResult.netIncome },
        { label: 'ภาษีก่อนเครดิต', value: this.currentResult.taxBeforeCredit },
        { label: 'หัก ณ ที่จ่าย', value: this.currentResult.withholdingTax },
      ];
    },
    categorySummary() {
      const seen = new Map();
      this.rulesForForm.forEach((rule) => {
        if (!rule.category || rule.category === 'INCOME_EXPENSE' || seen.has(rule.category)) return;
        seen.set(rule.category, {
          code: rule.category,
          label: rule.category_label || rule.category,
          icon: CATEGORY_ICONS[rule.category] || '📌',
        });
      });
      if (seen.size === 0) {
        this.deductionGroups.forEach((group) => {
          seen.set(group.id, { code: group.id, label: group.title, icon: group.icon });
        });
      }
      return Array.from(seen.values());
    },
  },
  watch: {
    yearOptions: {
      immediate: true,
      handler(options) {
        if (!this.selectedYear && options.length) this.selectedYear = options[0];
      },
    },
    WITHHOLDING_PAGE() {
      if (this.currentPage > this.WITHHOLDING_PAGE) this.currentPage = this.WITHHOLDING_PAGE;
    },
  },
  methods: {
    formatNum(n) {
      return formatNumber(n || 0);
    },
    formatDate(isoStr) {
      if (!isoStr) return '';
      return new Date(isoStr).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' });
    },
    parseNumericInput(value) {
      return Number(String(value || '').replace(/[^\d]/g, '')) || 0;
    },
    toNumericInput(value) {
      return Number(value || 0) > 0 ? Number(value).toLocaleString('th-TH') : '';
    },
    updateAmount(key, value) {
      this.taxInput[key] = this.parseNumericInput(value);
    },
    updateNumber(key, value) {
      this.taxInput[key] = this.parseNumericInput(value);
    },
    updateBoolean(key, value) {
      this.taxInput[key] = Boolean(value);
      if (key === 'spouseNoIncome') this.taxInput.hasSpouse = Boolean(value);
    },
    handleFormChange(value) {
      this.formType = value;
      this.taxInput.formType = value;
      this.currentPage = FORM_PAGE;
    },
    handleBack() {
      this.currentPage = Math.max(FORM_PAGE, this.currentPage - 1);
    },
    handleNext() {
      this.currentPage = Math.min(this.WITHHOLDING_PAGE, this.currentPage + 1);
    },
    resetForm() {
      this.taxInput = getDefaultTaxInput();
      this.formType = '90';
      this.currentPage = FORM_PAGE;
      clearResult();
      this.showSnackbar('Form reset', 'info');
    },
    getActualExpenseKey(sectionKey) {
      const map = {
        income40_5: 'expense40_5',
        income40_6: 'expense40_6',
        income40_7: 'expense40_7',
        income40_8: 'expense40_8',
      };
      return map[sectionKey] || '';
    },
    getExpenseChoiceValue(sectionKey) {
      if (sectionKey === 'income40_5') {
        if (this.taxInput.expense40_5 !== null) return 'actual';
        return `flat_${this.taxInput.expense40_5_rate}`;
      }
      if (sectionKey === 'income40_6') {
        if (this.taxInput.expense40_6 !== null) return 'actual';
        return `flat_${this.taxInput.expense40_6_rate}`;
      }
      if (sectionKey === 'income40_7') return this.taxInput.expense40_7 !== null ? 'actual' : 'flat_60';
      if (sectionKey === 'income40_8') return this.taxInput.expense40_8 !== null ? 'actual' : 'flat_60';
      return '';
    },
    isActualExpense(sectionKey) {
      return this.getExpenseChoiceValue(sectionKey) === 'actual';
    },
    handleExpenseChoiceChange(sectionKey, value) {
      if (sectionKey === 'income40_5') {
        if (value === 'actual') this.taxInput.expense40_5 = this.taxInput.expense40_5 ?? 0;
        else {
          this.taxInput.expense40_5 = null;
          this.taxInput.expense40_5_rate = Number(value.replace('flat_', ''));
        }
      }
      if (sectionKey === 'income40_6') {
        if (value === 'actual') this.taxInput.expense40_6 = this.taxInput.expense40_6 ?? 0;
        else {
          this.taxInput.expense40_6 = null;
          this.taxInput.expense40_6_rate = Number(value.replace('flat_', ''));
        }
      }
      if (sectionKey === 'income40_7') {
        this.taxInput.expense40_7 = value === 'actual' ? this.taxInput.expense40_7 ?? 0 : null;
      }
      if (sectionKey === 'income40_8') {
        this.taxInput.expense40_8 = value === 'actual' ? this.taxInput.expense40_8 ?? 0 : null;
      }
    },
    openSaveDialog() {
      this.scenarioName = '';
      this.saveDialog = true;
    },
    saveScenario() {
      if (!this.scenarioName.trim()) return;
      createScenario({
        name: this.scenarioName.trim(),
        formType: this.formType,
        input: { ...this.taxInput },
        result: { ...this.currentResult },
      });
      this.saveDialog = false;
      this.showSnackbar('Scenario saved', 'success');
    },
    loadScenario(scenario) {
      this.taxInput = { ...scenario.input };
      this.formType = scenario.formType || '90';
      this.currentPage = FORM_PAGE;
      this.showSnackbar(`Loaded: ${scenario.name}`, 'info');
    },
    deleteScenarioConfirm(scenario) {
      this.scenarioToDelete = scenario;
      this.deleteDialog = true;
    },
    confirmDelete() {
      if (this.scenarioToDelete) {
        deleteScenario(this.scenarioToDelete.id);
        this.showSnackbar('Scenario deleted', 'error');
      }
      this.deleteDialog = false;
      this.scenarioToDelete = null;
    },
    showSnackbar(text, color) {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.cfg-breadcrumb {
  padding: 12px 16px;
  font-size: 13px;
  color: #666;
}

.cfg-breadcrumb-sep {
  margin: 0 4px;
}

.cfg-breadcrumb-current {
  font-weight: 600;
  color: #333;
}

.sandbox-source-card {
  margin-bottom: 18px;
}

.sandbox-source-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #4b5563;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 12px;
}

.sandbox-source-grid {
  display: grid;
  grid-template-columns: minmax(170px, 220px) minmax(150px, 180px) max-content;
  gap: 10px;
  align-items: center;
}

.sandbox-source-field {
  min-width: 0;
  height: 40px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d8dde8;
  border-radius: 8px;
  background: #ffffff;
  padding: 0 10px;
  box-sizing: border-box;
}

.sandbox-source-field:focus-within {
  border-color: #1867c0;
  box-shadow: 0 0 0 3px rgba(24, 103, 192, 0.1);
}

.sandbox-source-field > span {
  position: absolute;
  top: -8px;
  left: 34px;
  max-width: calc(100% - 48px);
  background: #ffffff;
  color: #6b7280;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sandbox-source-field select {
  min-width: 0;
  flex: 1;
  height: 100%;
  border: 0;
  background: transparent;
  color: #111827;
  padding: 0;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  outline: none;
}

.sandbox-source-field strong {
  min-width: 0;
  color: #111827;
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sandbox-config-link {
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid rgba(24, 103, 192, 0.18);
  border-radius: 8px;
  background: rgba(24, 103, 192, 0.06);
  color: #135eb8;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.sandbox-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 22px;
  align-items: start;
}

.sandbox-main {
  min-width: 0;
}

.sandbox-mobile-frame {
  width: min(100%, 390px);
  min-height: 720px;
  margin: 0 auto 2rem;
  border: 8px solid #111827;
  border-radius: 34px;
  background: linear-gradient(180deg, #171a34 0%, #1f223d 48%, #171827 100%);
  box-shadow: 0 24px 58px rgba(15, 23, 42, 0.24);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.sandbox-phone-status {
  min-height: 40px;
  padding: 10px 18px 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(248, 248, 248, 0.82);
  font-size: 12px;
  font-weight: 700;
}

.sandbox-phone-chat {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 14px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
}

.sandbox-bot-bubble {
  align-self: flex-start;
  max-width: 270px;
  border-radius: 18px 18px 18px 6px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(248, 248, 248, 0.86);
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.45;
}

.sandbox-widget {
  width: 100%;
  max-width: 316px;
  align-self: flex-end;
  box-sizing: border-box;
  border-radius: 16px;
  background: #3e4661;
  color: #f8f8f8;
  box-shadow: 0 18px 34px rgba(0, 0, 0, 0.18);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.deduction-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(248, 248, 248, 0.58);
}

.deduction-progress span {
  max-width: 176px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress-bar {
  flex: 1;
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar > div {
  height: 100%;
  background: linear-gradient(90deg, #4f4c5f, #a39ec5);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.group-items {
  max-height: 474px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 6px 0;
  padding-right: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
}

.group-item {
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.group-item:last-child {
  border-bottom: none;
}

.group-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.item-label {
  font-size: 15px;
  font-weight: 500;
  color: rgba(248, 248, 248, 0.85);
  display: block;
  margin-bottom: 4px;
  line-height: 1.35;
}

.item-label.muted {
  opacity: 0.6;
}

.income-sublabel {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: rgba(163, 158, 197, 0.75);
  margin-top: 2px;
}

.max-hint {
  font-size: 13px;
  color: rgba(163, 158, 197, 0.7);
  margin-left: 6px;
  font-weight: 400;
}

.sandbox-widget input[type='text'] {
  background: rgba(25, 26, 50, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 10px 14px;
  color: #f8f8f8;
  font-size: 17px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
}

.sandbox-widget input[type='text']:focus {
  border-color: rgba(163, 158, 197, 0.5);
}

.form-type-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-type-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(25, 26, 50, 0.45);
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  text-align: left;
  color: #f8f8f8;
  font-family: inherit;
  width: 100%;
}

.form-type-btn:hover {
  background: rgba(163, 158, 197, 0.15);
  border-color: rgba(163, 158, 197, 0.4);
}

.form-type-btn.selected {
  border-color: #a39ec5;
  background: rgba(163, 158, 197, 0.2);
}

.form-type-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.form-type-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.form-type-title {
  font-size: 16px;
  font-weight: 700;
  color: #f8f8f8;
}

.form-type-desc {
  font-size: 14px;
  color: rgba(248, 248, 248, 0.6);
  font-weight: 400;
  line-height: 1.35;
}

.form-type-check {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border: 1.5px solid rgba(163, 158, 197, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #a39ec5;
  font-weight: 700;
  background: transparent;
}

.form-type-btn.selected .form-type-check {
  background: #a39ec5;
  color: #1e1e2e;
  border-color: #a39ec5;
}

.expense-choice-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.expense-choice-label {
  font-size: 14px;
  color: rgba(163, 158, 197, 0.8);
}

.expense-choice-select {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(163, 158, 197, 0.3);
  border-radius: 8px;
  color: #f8f8f8;
  padding: 5px 8px;
  font-size: 14.5px;
  cursor: pointer;
}

.expense-choice-select option {
  background: #2a2d45;
  color: #f8f8f8;
}

.expense-choice-select:focus,
.expense-actual-input:focus {
  outline: none;
  border-color: rgba(163, 158, 197, 0.7);
}

.expense-actual-input {
  border-radius: 8px !important;
  padding: 7px 9px !important;
  font-size: 15px !important;
}

.auto-item {
  background: rgba(163, 158, 197, 0.08);
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 2px;
}

.auto-item .item-label {
  margin-bottom: 0;
}

.auto-badge {
  background: rgba(163, 158, 197, 0.2);
  color: #a39ec5;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 13px;
  flex-shrink: 0;
}

.auto-amount {
  font-size: 16px;
  font-weight: 600;
  color: #a39ec5;
}

.expense-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 4px;
  padding-top: 10px;
  color: rgba(248, 248, 248, 0.82);
  font-size: 14px;
}

.expense-total-row strong {
  color: #a39ec5;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  cursor: pointer;
  padding: 3px 0;
  color: rgba(248, 248, 248, 0.78);
}

.checkbox-label input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: #a39ec5;
  cursor: pointer;
}

.field-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
}

.field-row label {
  font-size: 14px;
  color: rgba(248, 248, 248, 0.6);
  flex: 1;
}

.field-row input {
  width: 64px !important;
  text-align: center;
  height: 34px;
  font-size: 16px !important;
  border-radius: 8px !important;
  padding: 6px 8px !important;
}

.deduction-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  flex-shrink: 0;
}

.deduction-actions button {
  flex: 1;
  background: linear-gradient(135deg, #4f4c5f, #a39ec5);
  color: #fff;
  border: none;
  border-radius: 22px;
  padding: 10px;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.2s;
  font-family: inherit;
}

.deduction-actions button:hover {
  opacity: 0.85;
}

.deduction-actions button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.deduction-actions .skip-btn {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(248, 248, 248, 0.6);
}

.sandbox-home-indicator {
  width: 136px;
  height: 5px;
  margin: 0 auto 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.34);
  flex: 0 0 auto;
}

.sandbox-result-panel {
  position: sticky;
  top: 96px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sandbox-result-card,
.sandbox-result-list,
.sandbox-config-summary,
.sandbox-scenarios-card {
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
}

.sandbox-result-card {
  padding: 20px;
  background: linear-gradient(135deg, #171a34 0%, #1867c0 100%);
  color: #ffffff;
}

.sandbox-result-card span {
  display: block;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.78);
}

.sandbox-result-card strong {
  display: block;
  margin-top: 6px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.sandbox-result-card p {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
}

.sandbox-result-list {
  padding: 8px 16px;
}

.sandbox-result-list div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #edf0f5;
}

.sandbox-result-list div:last-child {
  border-bottom: 0;
}

.sandbox-result-list span {
  color: #6b7280;
  font-size: 14px;
}

.sandbox-result-list strong {
  color: #111827;
  font-size: 15px;
  font-weight: 600;
}

.sandbox-config-summary,
.sandbox-scenarios-card {
  padding: 16px;
}

.sandbox-config-summary h3,
.sandbox-scenarios-head h3 {
  margin: 0;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.sandbox-config-summary p {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 13.5px;
  line-height: 1.5;
}

.sandbox-config-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin: 10px 0;
}

.sandbox-config-kpis div {
  min-width: 0;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  background: #f8fafc;
  padding: 10px;
}

.sandbox-config-kpis span {
  display: block;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.sandbox-config-kpis strong {
  display: block;
  margin-top: 3px;
  color: #111827;
  font-size: 15px;
  font-weight: 700;
}

.sandbox-category-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.sandbox-category-list span {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.06);
  color: #374151;
  padding: 3px 9px;
  font-size: 12.5px;
  font-weight: 600;
}

.sandbox-scenarios-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sandbox-scenarios-head span {
  min-width: 28px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #eef2f7;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
}

.sandbox-empty {
  margin: 14px 0 0;
  color: #8a8f98;
  font-size: 14px;
  text-align: center;
}

.sandbox-scenario-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 0;
  border-bottom: 1px solid #edf0f5;
  background: transparent;
  padding: 10px 0;
  color: #111827;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.sandbox-scenario-row span {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sandbox-scenario-row strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.sandbox-scenario-row em {
  color: #6b7280;
  font-size: 12.5px;
  font-style: normal;
}

@media (max-width: 1180px) {
  .sandbox-layout {
    grid-template-columns: 1fr;
  }

  .sandbox-result-panel {
    position: static;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .sandbox-config-summary,
  .sandbox-scenarios-card {
    grid-column: 1 / -1;
  }
}

@media (max-width: 760px) {
  .sandbox-source-grid,
  .sandbox-result-panel {
    grid-template-columns: 1fr;
  }

  .sandbox-mobile-frame {
    border-radius: 16px;
  }
}
</style>
